import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { CHAPTERS, UI } from '../data/portfolio';
import { useLang } from '../i18n';

const ROUTES = { HOME: '/', WORK: '/work', ABOUT: '/about', CONTACT: '/contact' };

const LABELS = {
  HOME: { en: 'HOME', fr: 'ACCUEIL' },
  WORK: { en: 'WORK', fr: 'PROJETS' },
  ABOUT: { en: 'ABOUT', fr: 'À PROPOS' },
  CONTACT: { en: 'CONTACT', fr: 'CONTACT' },
};

const LINKS = ['HOME', 'WORK', 'ABOUT', 'CONTACT'];

export default function Nav() {
  const [workOpen, setWorkOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { t, lang, toggle } = useLang();

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-ink/10 bg-paper/85 backdrop-blur-md"
      onMouseLeave={() => setWorkOpen(false)}
    >
      <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-6 md:h-20 md:px-10">
        <Link
          to="/"
          data-testid="nav-brand"
          className="text-sm font-medium uppercase tracking-[0.28em] md:text-base"
        >
          Donatien Grimaud
        </Link>

        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((name) => {
            const active = pathname === ROUTES[name] || (name === 'WORK' && pathname.startsWith('/work'));
            return (
              <div key={name} onMouseEnter={() => setWorkOpen(name === 'WORK')}>
                <NavLink
                  to={ROUTES[name]}
                  data-testid={`nav-link-${name.toLowerCase()}`}
                  className={`group relative text-[11px] uppercase tracking-[0.28em] transition-colors duration-300 ${
                    active ? 'text-ink' : 'text-ink/60 hover:text-ink'
                  }`}
                >
                  {t(LABELS[name])}
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-ink transition-all duration-500 ${
                      active ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </NavLink>
              </div>
            );
          })}
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="border border-ink/30 px-3 py-1 text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-ink hover:text-paper"
          >
            {lang === 'en' ? 'FR' : 'EN'}
          </button>
        </nav>

        <div className="flex items-center gap-3 md:hidden">
          <button
            onClick={toggle}
            data-testid="lang-toggle-mobile"
            className="border border-ink/30 px-2.5 py-1 text-[10px] uppercase tracking-[0.25em]"
          >
            {lang === 'en' ? 'FR' : 'EN'}
          </button>
          <button
            className="md:hidden"
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      {/* Desktop WORK dropdown */}
      <AnimatePresence>
        {workOpen && (
          <motion.div
            data-testid="work-dropdown"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 top-full hidden border-b border-ink/10 bg-paper/95 backdrop-blur-md md:block"
            onMouseEnter={() => setWorkOpen(true)}
          >
            <div className="mx-auto grid max-w-[1600px] grid-cols-5 gap-0 px-10 py-8">
              {CHAPTERS.map((c, i) => (
                <Link
                  key={c.slug}
                  to={`/work/${c.slug}`}
                  data-testid={`work-chapter-${c.slug}`}
                  onClick={() => setWorkOpen(false)}
                  className="group border-l border-ink/10 px-5 py-2 transition-colors duration-300 hover:bg-white/60 first:border-l-0 first:pl-0"
                >
                  <div className="text-[10px] uppercase tracking-[0.25em] text-ink/50">
                    {t(UI.chapterLabel)} {c.numeral}
                  </div>
                  <div className="mt-2 text-xs font-medium uppercase tracking-[0.2em] transition-transform duration-300 group-hover:translate-x-1">
                    {c.title}
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-ink/50">
                    {t(c.tag)}
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu — portaled to body so the header's backdrop-filter
          containing block cannot clip it */}
      {createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              data-testid="mobile-menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 top-16 z-40 overflow-y-auto bg-paper px-6 pb-16 pt-6 md:hidden"
            >
              {LINKS.map((name, i) => (
                <motion.div
                  key={name}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.06, duration: 0.5 }}
                  className="border-b border-ink/10 py-5"
                >
                  <NavLink
                    to={ROUTES[name]}
                    data-testid={`mobile-link-${name.toLowerCase()}`}
                    onClick={() => setMobileOpen(false)}
                    className="text-2xl font-light uppercase tracking-[0.25em]"
                  >
                    {t(LABELS[name])}
                  </NavLink>
                  {name === 'WORK' && (
                    <div className="mt-4 space-y-3 pl-1">
                      {CHAPTERS.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/work/${c.slug}`}
                          data-testid={`mobile-chapter-${c.slug}`}
                          onClick={() => setMobileOpen(false)}
                          className="block text-[11px] uppercase tracking-[0.22em] text-ink/60"
                        >
                          {c.numeral} — {c.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
